import React, { useState } from 'react';

const inputStyle = {
  width: '100%',
  background: 'transparent',
  border: 'none',
  borderBottom: '2px solid rgba(156, 163, 175, 0.3)',
  borderRadius: 0,
  padding: '10px 0',
  fontFamily: 'var(--font-body)',
  fontSize: '14px',
  fontWeight: 400,
  color: 'var(--color-foreground)',
  outline: 'none',
  transition: 'border-color 250ms ease-out',
  letterSpacing: '0.01em',
};

const labelStyle = {
  fontFamily: 'var(--font-caption)',
  fontSize: '11px',
  fontWeight: 500,
  letterSpacing: '0.1em',
  textTransform: 'uppercase',
  color: 'var(--color-muted-foreground)',
  display: 'block',
  marginBottom: '8px',
};

const EmailVerification = ({ onVerified, onSwitchToBackup }) => {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!email) { setError('E-postadresse er påkrevd'); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(email)) { setError('Ugyldig e-postadresse (f.eks. navn@eksempel.no)'); return; }
    setIsLoading(true);
    await new Promise(r => setTimeout(r, 800));
    const users = JSON.parse(localStorage.getItem('veiassistent_users') || '[]');
    const user = users?.find(u => u?.email?.toLowerCase() === email?.toLowerCase());
    setIsLoading(false);
    if (!user) {
      setError('Ingen konto funnet med denne e-postadressen.');
      return;
    }
    setError('');
    onVerified(email, user);
  };

  return (
    <div>
      {/* Geometric accent line */}
      <div style={{ width: '48px', height: '3px', background: 'var(--color-accent)', marginBottom: '24px' }} />
      <h2 style={{ fontFamily: 'var(--font-heading)', fontSize: '28px', fontWeight: 700, color: 'var(--color-foreground)', marginBottom: '8px', lineHeight: 1.2 }}>
        Gjenopprett konto
      </h2>
      <p style={{ fontFamily: 'var(--font-body)', fontSize: '14px', fontWeight: 400, color: 'var(--color-muted-foreground)', marginBottom: '32px', lineHeight: 1.6 }}>
        Skriv inn e-postadressen din for å starte gjenopprettingsprosessen.
      </p>
      <form onSubmit={handleSubmit} noValidate>
        <div className="mb-6">
          <label htmlFor="recovery-email" style={labelStyle}>E-postadresse *</label>
          <input
            id="recovery-email"
            type="email"
            value={email}
            onChange={e => { setEmail(e?.target?.value); setError(''); }}
            placeholder="navn@eksempel.no"
            style={{ ...inputStyle, borderBottomColor: error ? '#FF5722' : 'rgba(156, 163, 175, 0.3)' }}
            onFocus={e => { if (!error) e.target.style.borderBottomColor = 'var(--color-primary)'; }}
            onBlur={e => { if (!error) e.target.style.borderBottomColor = 'rgba(156, 163, 175, 0.3)'; }}
          />
          {error && (
            <p style={{ fontFamily: 'var(--font-caption)', fontSize: '12px', fontWeight: 400, color: '#FF5722', marginTop: '6px', display: 'flex', alignItems: 'center', gap: '4px' }}>
              <span>⚠</span>{error}
            </p>
          )}
        </div>

        <button
          type="submit"
          disabled={isLoading}
          style={{
            width: '100%',
            background: isLoading ? 'rgba(255, 87, 34, 0.6)' : 'var(--color-accent)',
            border: '2px solid var(--color-accent)',
            borderRadius: 'var(--radius-sm)',
            padding: '14px 24px',
            fontFamily: 'var(--font-heading)',
            fontSize: '16px',
            fontWeight: 700,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            color: '#FFFFFF',
            cursor: isLoading ? 'not-allowed' : 'pointer',
            transition: 'all 250ms ease-out',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            marginBottom: '16px',
          }}
          onMouseEnter={e => { if (!isLoading) { e.currentTarget.style.background = '#E64A19'; e.currentTarget.style.borderColor = '#E64A19'; } }}
          onMouseLeave={e => { if (!isLoading) { e.currentTarget.style.background = 'var(--color-accent)'; e.currentTarget.style.borderColor = 'var(--color-accent)'; } }}
        >
          {isLoading ? (
            <>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ animation: 'spin 1s linear infinite' }}>
                <path d="M21 12a9 9 0 11-6.219-8.56" />
              </svg>
              Verifiserer...
            </>
          ) : 'Send gjenopprettingslenke'}
        </button>
      </form>
      {/* Backup code option */}
      <div
        style={{
          borderTop: '1px solid rgba(156, 163, 175, 0.15)',
          paddingTop: '20px',
          marginTop: '8px',
        }}
      >
        <p style={{ fontFamily: 'var(--font-body)', fontSize: '13px', color: 'var(--color-muted-foreground)', marginBottom: '10px' }}>
          Har du 2FA aktivert?
        </p>
        <button
          type="button"
          onClick={onSwitchToBackup}
          style={{
            background: 'transparent',
            border: '2px solid var(--color-primary)',
            borderRadius: 'var(--radius-sm)',
            padding: '10px 20px',
            fontFamily: 'var(--font-caption)',
            fontSize: '13px',
            fontWeight: 500,
            color: 'var(--color-primary)',
            cursor: 'pointer',
            transition: 'all 250ms ease-out',
            letterSpacing: '0.04em',
          }}
          onMouseEnter={e => { e.currentTarget.style.background = 'rgba(0, 79, 152, 0.1)'; }}
          onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
        >
          Bruk sikkerhetskode
        </button>
      </div>
      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } } input::placeholder { color: rgba(156, 163, 175, 0.5); }`}</style>
    </div>
  );
};

export default EmailVerification;
